import { useCallback, useEffect, useState } from "react";
import { createFileRoute, Link, useLocation } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, CheckCircle2, Download, Loader2, Lock, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DarSheetHeader,
  DarSheetEntries,
  type DarSheetPayload,
} from "@/components/dar/DarSheetView";
import {
  getDarSheet as getDarSheetFn,
  exportDarSheetPdf as exportDarSheetPdfFn,
} from "@/lib/dar.functions";

export const Route = createFileRoute("/_authenticated/dar/$reportId")({
  component: DarSheetPage,
  head: () => ({
    meta: [
      { title: "D.A.R. Shift Report — MissionReady" },
      {
        name: "description",
        content:
          "Finalized shift D.A.R. Sheet with every occurrence, transcript, AI summary and original recording.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function DarSheetPage() {
  const { reportId } = Route.useParams();
  const location = useLocation();
  const justSubmitted =
    (location.state as { justSubmitted?: boolean } | undefined)?.justSubmitted ?? false;
  const [view, setView] = useState<DarSheetPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showSubmittedBanner, setShowSubmittedBanner] = useState(justSubmitted);

  const getSheet = useServerFn(getDarSheetFn);
  const exportPdf = useServerFn(exportDarSheetPdfFn);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sheet = await getSheet({ data: { sheetId: reportId } });
      setView(sheet as DarSheetPayload);
    } catch (e: any) {
      toast.error(e?.message ?? "Could not load this D.A.R. Sheet");
    } finally {
      setLoading(false);
    }
  }, [getSheet, reportId]);

  useEffect(() => {
    void load();
  }, [load]);

  const download = async () => {
    setExporting(true);
    try {
      const { url } = (await exportPdf({ data: { sheetId: reportId } })) as { url: string };
      window.open(url, "_blank");
    } catch (e: any) {
      toast.error(e?.message ?? "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <Button asChild variant="ghost" size="sm" className="mb-3 -ml-2">
        <Link to="/dar">
          <ArrowLeft className="mr-1 h-4 w-4" /> D.A.R.
        </Link>
      </Button>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading report…
        </div>
      )}

      {!loading && view && (
        <div className="space-y-4">
          <DarSheetHeader view={view} />
          {showSubmittedBanner && view.sheet.submitted_at && (
            <div className="flex items-start justify-between gap-3 rounded-lg border border-green-500/30 bg-green-500/10 p-3 text-sm text-foreground">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                <span>
                  <strong>D.A.R. Shift Report submitted successfully.</strong>
                  <span className="block text-muted-foreground">
                    Locked and finalized on {new Date(view.sheet.submitted_at).toLocaleString()}.
                  </span>
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0"
                onClick={() => setShowSubmittedBanner(false)}
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Dismiss</span>
              </Button>
            </div>
          )}
          {view.sheet.status === "submitted" && (
            <p className="flex items-start gap-2 rounded-lg border border-primary/40 bg-primary/5 p-3 text-sm">
              <Lock className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>
                This D.A.R. Sheet was submitted and is locked. Every occurrence, transcript,
                summary, note, amendment and original recording stays available below.
                {view.sheet.submitted_at && (
                  <span className="mt-1 block text-xs text-muted-foreground">
                    Submitted at {new Date(view.sheet.submitted_at).toLocaleString()}.
                  </span>
                )}
              </span>
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={exporting}
              onClick={() => void download()}
            >
              {exporting ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="mr-1 h-3.5 w-3.5" />
              )}
              Export PDF
            </Button>
            <p className="w-full text-xs text-muted-foreground">
              The PDF is a printable representation. This digital sheet — with every original
              recording — remains the record of file.
            </p>
          </div>
          <DarSheetEntries view={view} allowAmend onChanged={() => void load()} />
        </div>
      )}
    </div>
  );
}
